import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from '../../../auth/useAuth0';
import { Client } from "../../../model/client/Client";
import { getConsumerCardByEmail, ConsumerCardView } from "../../../service/payment/payment-service";

/**
 * Read-only view of the client's card on file. The card is the CONSUMER's —
 * a single global card they manage themselves in the consumer app (Perfil →
 * Formas de pagamento). The admin only SEES the status (by the client's email);
 * there's no per-business card anymore.
 */
const brandLabel = (b?: string | null): string | null => {
    if (!b) return null; // caller falls back to the localized generic label
    const map: Record<string, string> = {
        visa: "Visa", mastercard: "Mastercard", amex: "Amex",
        discover: "Discover", diners: "Diners", jcb: "JCB", unionpay: "UnionPay",
    };
    return map[b.toLowerCase()] || b.charAt(0).toUpperCase() + b.slice(1);
};

interface Props {
    businessId: string;
    client: Client;
    /** kept for call-site compatibility — unused (read-only). */
    onChanged?: () => void;
}

const ClientCardSection: React.FC<Props> = ({ businessId, client }) => {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const [card, setCard] = useState<ConsumerCardView | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            if (!client.email) {
                if (alive) { setCard({ hasCard: false }); setLoading(false); }
                return;
            }
            try {
                const token = await getAccessTokenSilently();
                const c = await getConsumerCardByEmail(businessId, client.email, token);
                if (alive) setCard(c);
            } catch {
                if (alive) setCard({ hasCard: false });
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [businessId, client.email, getAccessTokenSilently]);

    if (!client.email) {
        return <p className="text-muted mb-0">{t("clients.noEmail")}</p>;
    }
    if (loading) {
        return <p className="text-muted mb-0">{t("common.loading")}</p>;
    }

    return (
        <div className="client-card-section" data-cy="clients-card-section">
            {card?.hasCard ? (
                <div className="client-card-onfile" data-cy="clients-card-onfile">
                    <div className="client-card-onfile__row">
                        <span className="client-card-onfile__brand" data-cy="clients-card-brand">{brandLabel(card.brand) ?? t("clients.cardDefaultLabel")}</span>
                        <span className="client-card-onfile__num" data-cy="clients-card-last4">•••• {card.last4}</span>
                        {card.expMonth && card.expYear && (
                            <span className="client-card-onfile__exp" data-cy="clients-card-expiry">
                                {String(card.expMonth).padStart(2, "0")}/{card.expYear}
                            </span>
                        )}
                    </div>
                </div>
            ) : (
                <p className="text-muted mb-0">{t("clients.noCard")}</p>
            )}
            <small className="form-text text-muted d-block mt-2">
                {t("clients.cardHint")}
            </small>
        </div>
    );
};

export default ClientCardSection;
